const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

const STORAGE_KEYS = {
  accessToken: 'tt_access_token',
  refreshToken: 'tt_refresh_token',
} as const

// Token storage
export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.accessToken)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.refreshToken)
}

function storeTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken)
  localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken)
}

export function clearTokens() {
  localStorage.removeItem(STORAGE_KEYS.accessToken)
  localStorage.removeItem(STORAGE_KEYS.refreshToken)
}

// Parse JWT payload without verification (client-side only)
export function parseToken(token: string): { sub: string; exp: number; email?: string; role?: string } | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload
  } catch {
    return null
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = parseToken(token)
  if (!payload) return true
  return payload.exp * 1000 < Date.now()
}

// API calls
interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  user: {
    id: string
    email: string
    display_name: string
    avatar_url?: string
    role: string
  }
}

export async function apiRegister(email: string, password: string, displayName: string): Promise<TokenResponse> {
  const res = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, display_name: displayName }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Registration failed' }))
    throw new Error(err.detail || `HTTP ${res.status}`)
  }
  const data: TokenResponse = await res.json()
  storeTokens(data.access_token, data.refresh_token)
  return data
}

export async function apiLogin(email: string, password: string): Promise<TokenResponse> {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Login failed' }))
    throw new Error(err.detail || `HTTP ${res.status}`)
  }
  const data: TokenResponse = await res.json()
  storeTokens(data.access_token, data.refresh_token)
  return data
}

export async function apiRefresh(): Promise<TokenResponse | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null

  const res = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
  if (!res.ok) {
    clearTokens()
    return null
  }
  const data: TokenResponse = await res.json()
  storeTokens(data.access_token, data.refresh_token)
  return data
}

export async function apiLogout(): Promise<void> {
  const refreshToken = getRefreshToken()
  if (refreshToken) {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).catch(() => {})
  }
  clearTokens()
}

/**
 * Get a valid access token, refreshing if needed.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const token = getAccessToken()
  if (token && !isTokenExpired(token)) {
    return token
  }

  // Try refresh
  const result = await apiRefresh()
  return result?.access_token ?? null
}
